<template>
  <section class="dp-layout">
    <section class="dp-left">
      <DepartmentPane directory-scope="school" @tree-node-click="treeNodeClick" :type="1" />
    </section>
    <section class="dp-right">
      <div class="repair-config-container">
      <el-card class="config-card" shadow="never">
        <div v-if="!departmentId" class="empty-text">请先在左侧选择学校</div>
        <div v-else v-loading="loading" class="config-body">
          <div class="config-header">
            <div class="config-header__text">
              <div class="config-header__title">
                <h3>报修流程模式配置</h3>
                <el-tag class="school-badge" effect="plain" type="success">
                  <i class="el-icon-school school-badge__icon" />
                  {{ departmentName || "未选择学校" }}
                </el-tag>
              </div>
              <p class="subtitle">选择该学校新报修单默认执行的流程模式</p>
            </div>
            <el-button plain icon="el-icon-view" @click="openFlowPreview('current')">查看流程图</el-button>
          </div>

          <el-form label-width="96px" class="config-form">
            <el-form-item label="流程模式">
              <p class="hint">
                切换仅影响新提交的报修单；进行中的单据按创建时快照模式继续执行。
              </p>
              <div class="mode-compare">
                <div class="mode-compare__item" :class="{ 'is-active': workflowMode === 'platform' }" @click="selectWorkflowMode('platform')">
                  <div class="mode-compare__head">
                    <span class="mode-compare__title">
                      平台标准模式
                      <el-tag v-if="workflowMode === 'platform'" size="mini" type="success" class="active-tag">当前已选</el-tag>
                    </span>
                    <el-link type="primary" :underline="false" @click.stop="openFlowPreview('platform')">查看</el-link>
                  </div>
                  <p class="mode-compare__desc">教师报修 → 初审 → 师傅检测/报价 → 费用审批 → 完工</p>
                </div>
                <div class="mode-compare__item mode-compare__item--voc" :class="{ 'is-active': workflowMode === 'vocational' }" @click="selectWorkflowMode('vocational')">
                  <div class="mode-compare__head">
                    <span class="mode-compare__title">
                      中职模式
                      <el-tag v-if="workflowMode === 'vocational'" size="mini" type="success" class="active-tag">当前已选</el-tag>
                    </span>
                    <el-link type="primary" :underline="false" @click.stop="openFlowPreview('vocational')">查看</el-link>
                  </div>
                  <p class="mode-compare__desc">教师报修 → 初审末级合并填表（类型/师傅/费用）→ 费用审批（0元跳过）→ 完工</p>
                </div>
              </div>
            </el-form-item>

            <el-form-item label="完工验收照片">
              <div class="completion-media-config">
                <div class="completion-media-config__switch-row">
                  <el-switch
                    v-model="requireCompletionMedia"
                    active-color="#13ce66"
                    inactive-color="#ff4949"
                    :active-text="requireCompletionMedia ? '强制必填' : '选填（允许直接结单）'"
                    @change="onMediaSwitchChange" />
                  <el-tag size="mini" :type="requireCompletionMedia ? 'warning' : 'success'" class="completion-tag">
                    {{ requireCompletionMedia ? '严格验收模式' : '灵活闭环模式' }}
                  </el-tag>
                </div>
                <p class="hint completion-hint">
                  <template v-if="requireCompletionMedia">
                    维修师傅完工提交时，必须上传至少 1 张现场维修照片；适用于要求严格验收现场成果的学校。
                  </template>
                  <template v-else>
                    维修人员点击提交直接完成审批闭环，现场照片与备注不要求必填。
                  </template>
                </p>
              </div>
            </el-form-item>
          </el-form>

          <div class="config-actions">
            <el-button type="primary" :loading="saving" @click="saveConfig">保存配置</el-button>
          </div>
        </div>
      </el-card>
      </div>

      <el-dialog
        title="报修流程图预览"
        :visible.sync="flowPreviewVisible"
        width="780px"
        append-to-body>
        <div class="flow-current">
          当前配置：
          <el-tag size="mini" type="success">{{ workflowMode === "vocational" ? "中职模式" : "平台标准模式" }}</el-tag>
        </div>
        <div class="flow-diff-alert-wrap">
          <el-alert
            class="flow-diff-alert"
            :class="{ 'is-muted': flowPreviewMode === workflowMode }"
            :type="flowPreviewMode === workflowMode ? 'info' : 'warning'"
            :closable="false"
            show-icon
            :title="flowPreviewMode === workflowMode
              ? `当前正在浏览「${flowPreviewMode === 'vocational' ? '中职模式' : '平台标准模式'}」流程图`
              : `当前配置是「${workflowMode === 'vocational' ? '中职模式' : '平台标准模式'}」，你正在浏览「${flowPreviewMode === 'vocational' ? '中职模式' : '平台标准模式'}」流程图`" />
        </div>
        <div class="flow-tabs">
          <el-radio-group v-model="flowPreviewMode" size="small">
            <el-radio-button label="platform">平台标准模式</el-radio-button>
            <el-radio-button label="vocational">中职模式</el-radio-button>
          </el-radio-group>
        </div>

        <div v-if="flowPreviewMode === 'platform'" class="flow-board">
          <div class="flow-step">教师报修</div>
          <div class="flow-arrow">→</div>
          <div class="flow-step">初审</div>
          <div class="flow-arrow">→</div>
          <div class="flow-step">派单 / 检测</div>
          <div class="flow-arrow">→</div>
          <div class="flow-step">费用审批</div>
          <div class="flow-arrow">→</div>
          <div class="flow-step flow-step--ok">维修完工</div>
        </div>

        <div v-else class="flow-board flow-board--voc">
          <div class="flow-step">教师报修</div>
          <div class="flow-arrow">→</div>
          <div class="flow-step flow-step--focus">初审（末级合并填表）</div>
          <div class="flow-arrow">→</div>
          <div class="flow-branch">
            <div class="flow-branch__title">总费用判断</div>
            <div class="flow-branch__row">
              <el-tag size="mini" type="success">= 0 元</el-tag>
              <span>直接进入维修</span>
            </div>
            <div class="flow-branch__row">
              <el-tag size="mini" type="warning">&gt; 0 元</el-tag>
              <span>进入费用审批</span>
            </div>
          </div>
          <div class="flow-arrow">→</div>
          <div class="flow-step flow-step--ok">维修完工</div>
        </div>
      </el-dialog>
    </section>
  </section>
</template>

<script>
import DepartmentPane from "../../components/DepartmentPane";
import { getRepairSchoolConfig, setRepairSchoolConfig } from "../../api/modules/repair";

export default {
  name: "RepairSchoolConfig",
  components: { DepartmentPane },
  data() {
    return {
      departmentId: 0,
      departmentName: "",
      workflowMode: "platform",
      requireCompletionMedia: true,
      hasUserModifiedMediaReq: false,
      loading: false,
      saving: false,
      flowPreviewVisible: false,
      flowPreviewMode: "platform"
    };
  },
  methods: {
    treeNodeClick(data) {
      this.departmentId = data.value || 0;
      this.departmentName = data.label || "";
      if (this.departmentId > 0) {
        this.loadConfig();
      }
    },
    loadConfig() {
      this.loading = true;
      getRepairSchoolConfig({ departmentId: this.departmentId })
        .then((res) => {
          if (res.data && res.data.success && res.data.response) {
            const resp = res.data.response;
            const mode = resp.WorkflowMode || resp.workflowMode || "platform";
            this.workflowMode = mode === "vocational" ? "vocational" : "platform";
            this.requireCompletionMedia = resp.RequireCompletionMedia != null
              ? !!resp.RequireCompletionMedia
              : this.workflowMode !== "vocational";
            this.hasUserModifiedMediaReq = false;
          } else {
            this.workflowMode = "platform";
            this.requireCompletionMedia = true;
            this.hasUserModifiedMediaReq = false;
          }
        })
        .catch(() => {
          this.$message.error("加载配置失败");
          this.workflowMode = "platform";
          this.requireCompletionMedia = true;
          this.hasUserModifiedMediaReq = false;
        })
        .finally(() => {
          this.loading = false;
        });
    },
    saveConfig() {
      if (!this.departmentId) {
        this.$message.warning("请先选择学校");
        return;
      }

      const modeText = this.workflowMode === "vocational" ? "中职模式" : "平台标准模式";
      const mediaText = this.requireCompletionMedia ? "完工必须拍照" : "完工照片选填";
      this.$confirm(`确认保存该校配置吗？（流程模式：${modeText}，${mediaText}）`, "二次确认", { type: "warning" })
        .then(() => {
          this.saving = true;
          return setRepairSchoolConfig({
            DepartmentId: this.departmentId,
            WorkflowMode: this.workflowMode,
            RequireCompletionMedia: this.requireCompletionMedia
          });
        })
        .then((res) => {
          if (res && res.data && res.data.success) {
            this.$message.success("保存成功");
            this.loadConfig();
          } else {
            this.$message.error((res && res.data && res.data.msg) || "保存失败");
          }
        })
        .catch((err) => {
          if (err !== "cancel") this.$message.error("保存失败");
        })
        .finally(() => {
          this.saving = false;
        });
    },
    openFlowPreview(mode) {
      if (mode === "platform" || mode === "vocational") {
        this.flowPreviewMode = mode;
      } else {
        this.flowPreviewMode = this.workflowMode === "vocational" ? "vocational" : "platform";
      }
      this.flowPreviewVisible = true;
    },
    selectWorkflowMode(mode) {
      const isVoc = mode === "vocational";
      this.workflowMode = isVoc ? "vocational" : "platform";
      if (!this.hasUserModifiedMediaReq) {
        this.requireCompletionMedia = !isVoc;
      }
    },
    onMediaSwitchChange() {
      this.hasUserModifiedMediaReq = true;
    }
  }
};
</script>

<style scoped>
.repair-config-container {
  width: 100%;
  max-width: 960px;
}

.config-card {
  border-radius: 12px;
  width: 100%;
}

.config-body {
  width: 100%;
}

.config-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 16px;
  margin-bottom: 20px;
  padding-bottom: 16px;
  border-bottom: 1px solid #eef2f7;
}

.config-header__text {
  flex: 1 1 auto;
  min-width: 0;
}

.config-header__title {
  display: flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 10px;
}

.config-header h3 {
  margin: 0;
  font-size: 20px;
  font-weight: 700;
  color: #1f2d3d;
}

.school-badge {
  height: auto;
  line-height: 1.4;
  padding: 4px 10px;
  border-radius: 8px;
  font-size: 13px;
  font-weight: 500;
}

.school-badge__icon {
  margin-right: 4px;
  font-size: 13px;
}

.subtitle {
  margin: 8px 0 0;
  color: #64748b;
  font-size: 13px;
  line-height: 1.5;
}

.config-form {
  margin-bottom: 0;
}

.hint {
  margin: 0 0 12px;
  color: #64748b;
  font-size: 13px;
  line-height: 1.6;
}

.mode-compare {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  width: 100%;
}

.mode-compare__item {
  border: 1px solid #e9edf5;
  border-radius: 10px;
  background: #f8faff;
  padding: 14px;
  transition: all 0.2s;
  cursor: pointer;
  min-width: 0;
}

.mode-compare__item--voc {
  background: #f7fbff;
  border-color: #dcecff;
}

.mode-compare__item.is-active {
  border-color: #67c23a;
  box-shadow: 0 0 0 2px rgba(103, 194, 58, 0.12);
  background: #f5fff5;
}

.mode-compare__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 8px;
  margin-bottom: 8px;
}

.mode-compare__title {
  display: inline-flex;
  align-items: center;
  flex-wrap: wrap;
  gap: 6px;
  font-size: 14px;
  font-weight: 600;
  color: #303133;
}

.active-tag {
  margin-left: 0;
}

.mode-compare__desc {
  margin: 0;
  color: #606266;
  font-size: 13px;
  line-height: 1.6;
}

.config-actions {
  display: flex;
  justify-content: flex-start;
  margin-top: 8px;
  padding-top: 20px;
  border-top: 1px solid #eef2f7;
}

.empty-text {
  color: #909399;
  min-height: 140px;
  display: flex;
  align-items: center;
  justify-content: center;
}

@media (max-width: 900px) {
  .mode-compare {
    grid-template-columns: 1fr;
  }

  .config-header {
    flex-direction: column;
    align-items: stretch;
  }
}

@media (max-width: 768px) {
  .config-card >>> .el-form-item {
    display: flex;
    flex-direction: column;
    align-items: flex-start !important;
    margin-bottom: 0;
  }

  .config-card >>> .el-form-item__label {
    width: 100% !important;
    text-align: left !important;
    padding: 0 0 8px !important;
    line-height: 1.35 !important;
    font-weight: 600;
    color: #334155;
  }

  .config-card >>> .el-form-item__content {
    margin-left: 0 !important;
    width: 100% !important;
  }
}
.flow-tabs {
  margin-bottom: 14px;
}
.flow-current {
  margin-bottom: 10px;
  color: #606266;
  font-size: 13px;
}
.flow-diff-alert {
  margin-bottom: 0;
}
.flow-diff-alert-wrap {
  margin-bottom: 12px;
  min-height: 40px;
}
.flow-diff-alert.is-muted {
  opacity: 0.72;
}
.flow-board {
  background: #f8fbff;
  border: 1px solid #e6eef9;
  border-radius: 12px;
  padding: 20px 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 8px;
}
.flow-board--voc {
  background: #f8fcff;
}
.flow-step {
  min-width: 104px;
  padding: 10px 12px;
  border-radius: 8px;
  background: #fff;
  border: 1px solid #d9e3f0;
  color: #303133;
  text-align: center;
  font-size: 13px;
}
.flow-step--focus {
  border-color: #9bc2ff;
  background: #edf5ff;
}
.flow-step--ok {
  border-color: #a9e2bc;
  background: #eefcf3;
}
.flow-arrow {
  color: #7f8ea3;
  font-size: 18px;
  margin: 0 2px;
}
.flow-branch {
  min-width: 180px;
  border: 1px dashed #c7d7ee;
  border-radius: 8px;
  background: #fff;
  padding: 8px 10px;
}
.flow-branch__title {
  font-size: 12px;
  color: #7f8ea3;
  margin-bottom: 6px;
}
.flow-branch__row {
  display: flex;
  align-items: center;
  gap: 8px;
  font-size: 12px;
  color: #606266;
  margin-bottom: 4px;
}
.flow-branch__row:last-child {
  margin-bottom: 0;
}

.completion-media-config {
  background: #f8fafc;
  border: 1px solid #e2e8f0;
  border-radius: 8px;
  padding: 12px 16px;
}

.completion-media-config__switch-row {
  display: flex;
  align-items: center;
  gap: 12px;
  margin-bottom: 8px;
}

.completion-tag {
  font-weight: 500;
}

.completion-hint {
  margin: 0;
  font-size: 12px;
  color: #64748b;
  line-height: 1.5;
}
</style>
